import { motion } from "framer-motion";
import { useEffect, useRef } from "react";

import SmartLink from "@/components/SmartLink";
import { Button } from "@/components/ui/button";
import { iconRegistry } from "@/content/iconRegistry";
import { trackCheckoutClick, trackPricingView } from "@/lib/conversionTracking";

export default function PricingSection({ content, dataTestId, checkoutProduct = "workshop", sectionId = "pricing" }) {
  const sectionRef = useRef(null);
  const hasTrackedPricingView = useRef(false);

  useEffect(() => {
    if (!sectionRef.current || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasTrackedPricingView.current) {
          return;
        }

        hasTrackedPricingView.current = true;
        trackPricingView({
          product: checkoutProduct,
          location: sectionId,
        });
        observer.disconnect();
      },
      {
        threshold: 0.5,
      },
    );

    observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, [checkoutProduct, sectionId]);

  return (
    <section ref={sectionRef} className="py-16 md:py-24" id={sectionId} data-testid={dataTestId}>
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h2 className="font-sans text-3xl md:text-4xl font-bold text-foreground mb-4">{content.title}</h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-card rounded-3xl p-8 md:p-12 shadow-medium border-2 border-secondary/45"
        >
          <div className="text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary/36 text-highlight font-body text-sm font-medium mb-6 border border-secondary/45">
              {content.badge}
            </span>

            {content.price ? (
              <p className="font-sans text-4xl md:text-5xl font-bold text-foreground mb-6">{content.price}</p>
            ) : null}

            <div className="space-y-3 mb-8">
              {content.items.map((item) => {
                const Icon = iconRegistry.CheckCircle;

                return (
                  <div key={item} className="flex items-center justify-center gap-2 font-body text-muted-foreground">
                    <Icon className="w-5 h-5 text-highlight" />
                    <span>{item}</span>
                  </div>
                );
              })}
            </div>

            <Button
              asChild
              size="lg"
              className="w-full max-w-full rounded-full bg-primary hover:bg-primary/90 text-white font-medium px-4 py-3 sm:w-auto sm:px-10 btn-hover"
            >
              <SmartLink
                href={content.primaryCta.href}
                external={content.primaryCta.external}
                onClick={() =>
                  trackCheckoutClick({
                    product: checkoutProduct,
                    label: content.primaryCta.label,
                    href: content.primaryCta.href,
                    location: "pricing",
                  })
                }
              >
                {content.primaryCta.label}
              </SmartLink>
            </Button>

            {content.footerNote ? <p className="font-body text-sm text-muted-foreground mt-6">{content.footerNote}</p> : null}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
